/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Match Booster Overview", description = "Increase match rates for your audiences and conversion events in advertising platforms.", lastUpdatedDate = new Date(1695254400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Match Booster Overview",
        description: "Increase match rates for your audiences and conversion events in advertising platforms.",
        lastUpdatedDate: "Thu Sep 21 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Modern Warehouse-Native Onboarding",
          "depth": 0,
          "id": "modern-warehouse-native-onboarding"
        }, {
          "value": "How does Match Booster work?",
          "depth": 0,
          "id": "how-does-match-booster-work"
        }, {
          "value": "Data vendors and data validation",
          "depth": 0,
          "id": "data-vendors-and-data-validation"
        }, {
          "value": "Supported destinations",
          "depth": 0,
          "id": "supported-destinations"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      h2: "h2",
      ol: "ol"
    }, _provideComponents(), props.components), {BtOnly, PylonLink} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Match Booster",
        extraText: " as an add-on product."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch ", _jsx(_components.strong, {
          children: "Match Booster"
        }), " is a suite of built-in data onboarding products that enhance the match rates of your audiences and conversion events on ", _jsx(_components.a, {
          href: "#supported-destinations",
          children: "various paid advertising platforms"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "The following are common use cases for Match Booster:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom audience targeting"
          }), ": increase the reach of your targeting and retargeting campaigns"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Suppression"
          }), ": ensure your existing customers are excluded from your campaigns"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Conversion events"
          }), ": get proper attribution and feedback for your campaigns"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Anonymous targeting"
          }), ": target anonymous visitors on your digital properties"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Match Booster enriches your model (eg., first-party data) with additional identifiers from third-party data partners that Hightouch has contracted with. These enriched identifiers help ad platforms better match their limited identity data with your first-party data. Ultimately, this enables better targeting, increased ROAS, and more accurate attribution."
      }), "\n", _jsx(_components.h2, {
        id: "modern-warehouse-native-onboarding",
        children: "Modern Warehouse-Native Onboarding"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Match Booster"
        }), " is built on a modern, warehouse-native architecture that eliminates the need for data replication. Your data is never persisted on Hightouch infrastructure, besides a hash of the primary key of your model."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch achieves this by using data sharing and storing a full copy of third-party data partners on our premises. The third-party data is queried and appended to your first-party data at sync time. This means you never have to store regulated third-party data in your infrastructure, and Hightouch never has to store your first-party data on our premises."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Warehouse-Native Benefits"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Security"
          }), ": since your first-party data is never stored with an intermediary, you significantly reduce security and privacy risks."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Direct data access"
          }), ": Match Booster queries your warehouse directly. This means whenever a new data point is added to your first-party data, you get immediate access. No extra pipelines are needed."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Faster refreshes"
          }), ": Hightouch infrastructure supports enrichment and syncing on an hourly and daily basis. This means you don't need to wait weeks for your data to be onboarded."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Simpler workflow"
          }), ": since Hightouch is a simple enrichment and pass-through layer, you avoid all the headaches of maintaining segments in another tool."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "how-does-match-booster-work",
        children: "How does Match Booster work?"
      }), "\n", _jsx(_components.p, {
        children: "Match Booster increases match rates by enriching first-party data with two types of additional identifiers sourced from third-party vendors."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Offline data"
          }), " such as email addresses and phone numbers"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Online data"
          }), " such as device IDs and cookie IDs"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch uses the additional identifiers to build a custom ", _jsx(_components.a, {
          href: "https://blog.hubspot.com/service/identity-graph",
          children: "identity graph"
        }), " for each ", _jsx(_components.a, {
          href: "#model-configuration",
          children: "model that you've enabled Match Booster on"
        }), ".\nYou can then configure your syncs to the ", _jsx(_components.a, {
          href: "#supported-destinations",
          children: "supported destinations"
        }), " to include your desired identifiers."]
      }), "\n", _jsx(_components.p, {
        children: "In this architecture, your data never leaves your environment for the purposes of enrichment. Additional identifiers are appended in-flight during a sync to the destination."
      }), "\n", _jsx(_components.h2, {
        id: "data-vendors-and-data-validation",
        children: "Data vendors and data validation"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Data Vendors"
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch sources third-party data from leading brand-name providers and cutting-edge emerging startups. Hightouch stores a full copy of third-party identity graphs on our premises so that your data does not need to leave your environment."
      }), "\n", _jsx(_components.p, {
        children: "The list of data vendors that Hightouch works with will be made available in any contractual agreement."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Data Cleaning"
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch rigorously cleans data provided from third-party vendors. These processes include filtering out abnormally large profiles, observing the frequency and recency of observed device linkages, prioritizing verified data (like emails), and using additional data points (like IP addresses, names) to filter out outliers."
      }), "\n", _jsx(_components.h2, {
        id: "supported-destinations",
        children: "Supported destinations"
      }), "\n", _jsx(_components.p, {
        children: "You can use Match Booster on syncs to the following destinations:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Custom Audiences"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/amazon-ads",
            children: "Amazon Ads DSP"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/criteo",
            children: "Criteo"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/digital-turbine",
            children: "Digital Turbine"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/dynata",
            children: "Dynata"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/facebook",
            children: "Facebook Custom Audiences"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/google",
            children: "Google Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/display-video",
            children: "Google Display & Video 360"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/inmobi",
            children: "InMobi"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/ispot-tv",
            children: "iSpot.tv"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/liveintent",
            children: "LiveIntent"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/lg-ad-solutions",
            children: "LG Ad Solutions"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/linkedin",
            children: "LinkedIn Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/bingads",
            children: "Microsoft Bing Ads Audiences"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/pinterest-ads",
            children: "Pinterest Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/reddit-ads",
            children: "Reddit Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/rokt",
            children: "Rokt"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/roku-ads",
            children: "Roku Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/samsung-ads",
            children: "Samsung Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/snapchat",
            children: "Snapchat"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/stackadapt",
            children: "StackAdapt"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/taboola",
            children: "Taboola"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/tiktok",
            children: "TikTok"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/tradedesk",
            children: "The Trade Desk"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/twitter",
            children: "Twitter"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/vistar-media",
            children: "Vistar Media"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/vizio-ads",
            children: "Vizio Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/yahoo",
            children: "Yahoo DSP"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Conversion Events"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/facebook-conversions",
            children: "Facebook Conversions"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/google",
            children: "Google Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/tiktok",
            children: "TikTok"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The catalog of supported destinations is continually growing. Please ", _jsx(PylonLink, {
          pylonText: "Hello, I have a feature request. I'd like Match Booster to work with ${platform}.",
          linkText: "let us know"
        }), " if there are additional ad platforms you're interested in using Match Booster on."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
